import EventContentState from "../../../components/EventContentState";
import {eventLinkProperties} from '../../../services/embedded-events';
import React, { useState, useEffect } from 'react';
import { ASSET_URL } from "../../../config";
import { MapPin, Calendar, ExternalLink, Navigation, Map as MapIcon, Compass } from 'lucide-react';
import { t } from "@lingui/core/macro";
import { useEvents } from "../../../services/EventService";
import EventAiContent from "../../../components/EventAiContent";
import AnnualEventPage from "../../../components/AnnualEventPage";
import {applyLang} from "../../../i18n";
import { formatEventDate } from "../../../components/eventDate";

const EventPage = ({ context }) => {
    const { org } = context;
    const { events, loading, error, retry } = useEvents(context);
    // L'événement (affiche ou PE) est résolu via le fetch client des événements.
    const eventKey = context.page?.eventId || context.route[1];
    const rawItem = (events || []).find(i => i.id === eventKey);
    const item = rawItem ? applyLang(rawItem, context.lang) : null;

    const {
        name = "",
        description = "",
        content = "",
        image = null,
        adresse = "",
        links = []
    } = item || {};

    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Pendant le chargement des événements (fetch client), ne rien afficher.
    if (loading || error || !item) return <EventContentState loading={loading} error={error} retry={retry}/>;
    if (item?.annualEventPage) return <AnnualEventPage event={item} context={context}/>;

    // Période quand le début et la fin ne tombent pas le même jour (PE uniquement,
    // cf. components/eventDate.js).
    const eventDateText = formatEventDate(item, context.langValue || context.lang?.value || context.lang);

    const hasImage = !!image;
    const hasLinks = links && links.length > 0;
    const imageLink = hasImage ? (image.startsWith("http") ? image : ASSET_URL + image) : null;

    const encodedAddress = encodeURIComponent(adresse);

    // Liens profonds corrigés
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    const wazeUrl = `https://waze.com/ul?q=${encodedAddress}`;
    const appleMapsUrl = `maps://maps.apple.com/?q=${encodedAddress}`;

    const s = {
        container: {
            fontFamily: 'Inter, system-ui, sans-serif',
            maxWidth: '1100px',
            margin: '0 auto',
            padding: isMobile ? '20px' : '60px 20px',
            color: '#1a1a1a',
        },
        grid: {
            display: 'grid',
            gridTemplateColumns: isMobile || !hasImage ? '1fr' : '420px 1fr',
            gap: isMobile ? '30px' : '60px',
            alignItems: 'start',
        },
        poster: {
            width: '100%',
            borderRadius: '16px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            objectFit: 'cover',
            aspectRatio: '3/4',
            backgroundColor: '#f5f5f5',
        },
        badge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '100px',
            fontSize: '0.9rem',
            fontWeight: '600',
            color: '#475569',
            marginBottom: '20px',
        },
        title: {
            fontSize: isMobile ? '2.2rem' : '3.5rem',
            fontWeight: '850',
            letterSpacing: '-0.03em',
            margin: '0 0 20px 0',
            lineHeight: '1',
            color: '#0f172a'
        },
        addressLink: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            color: '#64748b',
            textDecoration: 'none',
            fontSize: '1.1rem',
            marginBottom: '32px',
            transition: 'color 0.2s',
        },
        content: {
            fontSize: '1.15rem',
            lineHeight: '1.8',
            color: '#334155',
        },
        actionSection: {
            marginTop: '48px',
            padding: '24px',
            backgroundColor: '#f8fafc',
            borderRadius: '16px',
            border: '1px solid #f1f5f9'
        },
        linkRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 0',
            borderBottom: '1px solid #e2e8f0',
            textDecoration: 'none',
            color: '#2563eb',
            fontWeight: '600',
        },
        mapWrapper: {
            marginTop: '40px',
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            border: '1px solid #e2e8f0',
        },
        gpsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            backgroundColor: '#fff',
        },
        gpsBtn: {
            padding: '16px 8px',
            textAlign: 'center',
            fontSize: '0.75rem',
            textDecoration: 'none',
            color: '#1e293b',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            fontWeight: '600',
            borderRight: '1px solid #f1f5f9',
            transition: 'background 0.2s',
        }
    };

    return (
        <div style={s.container}>
            <div style={s.grid}>

                {/* SECTION GAUCHE : AFFICHE PORTRAIT */}
                {hasImage && (
                    <div style={{ position: isMobile ? 'static' : 'sticky', top: '40px' }}>
                        <img
                            src={imageLink}
                            alt={name}
                            style={s.poster}
                            onError={(e) => e.target.style.display = 'none'}
                        />
                    </div>
                )}

                {/* SECTION DROITE : INFOS */}
                <div>
                    {eventDateText && (
                        <div style={s.badge}>
                            <Calendar size={16} color="#2563eb" />
                            {eventDateText}
                        </div>
                    )}

                    <h1 style={s.title}>{name}</h1>

                    {adresse && (
                        <a href={googleMapsUrl} target="_blank" rel="noreferrer" style={s.addressLink}>
                            <MapPin size={22} style={{ color: '#ef4444', flexShrink: 0 }} />
                            <span>{adresse}</span>
                        </a>
                    )}

                    {description && (
                        <p style={{ fontSize: '1.25rem', color: '#475569', marginBottom: '32px', borderLeft: '4px solid #e2e8f0', paddingLeft: '20px' }}>
                            {description}
                        </p>
                    )}

                    <div style={s.content} dangerouslySetInnerHTML={{ __html: content }} />

                    {/* LIENS */}
                    {hasLinks && (
                        <div style={s.actionSection}>
                            <h3 style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8', marginBottom: '8px' }}>
                                {t`Liens & Inscriptions`}
                            </h3>
                            {links.map((link, i) => (
                                <a key={i} {...eventLinkProperties(link.url,context)} style={{...s.linkRow, borderBottom: i === links.length - 1 ? 'none' : s.linkRow.borderBottom}}>
                                    <span>{link.name}</span>
                                    <ExternalLink size={18} />
                                </a>
                            ))}
                        </div>
                    )}

                    {/* CARTE & NAVIGATION */}
                    {adresse && (
                        <div style={s.mapWrapper}>
                            <div style={s.gpsGrid}>
                                <a href={googleMapsUrl} target="_blank" rel="noreferrer" style={s.gpsBtn}>
                                    <img src="/img/maps/icons8-google-maps-96.png" width="34" height="34" alt="Google" />
                                    Google Maps
                                </a>
                                <a href={wazeUrl} target="_blank" rel="noreferrer" style={s.gpsBtn}>
                                    <img src="/img/maps/waze.png" width="34" height="34" alt="Waze" style={{borderRadius:"20%"}} />
                                    Waze
                                </a>
                                <a href={appleMapsUrl} target="_blank" rel="noreferrer" style={{...s.gpsBtn, borderRight: 'none'}}>
                                    <img src="/img/maps/icons8-apple-map-96.png" width="34" height="34" alt="Apple" />
                                    Apple Maps
                                </a>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <EventAiContent event={item} context={context} style={{ marginTop: isMobile ? 32 : 52 }} />
        </div>
    );
};

export default EventPage;
